import React from "react";
import {
  CreateMaintenanceTaskDto,
  EquipmentDto,
  IntervalUnit,
  LeadTimeUnit,
  MaintenanceCategory,
  MaintenancePriority,
  MaintenanceTaskDto,
  MAINTENANCE_CATEGORIES,
  MAINTENANCE_PRIORITIES,
  RecurrenceType,
  equipmentApi,
  maintenanceTasksApi,
  LEAD_TIME_UNITS,
  INTERVAL_UNITS,
} from "../../../services/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RecurrenceEditor } from "../components/RecurrenceEditor";
import { ReminderEditor, remindersToApi } from "../components/ReminderEditor";
import {
  CATEGORY_LABELS, PRIORITY_LABELS,
  categoryToInt, priorityToInt, recurrenceToInt, intervalUnitToInt,
  intToCategory, intToPriority, intToRecurrence, intToIntervalUnit, intToLeadTimeUnit,
} from "../utils";

interface Props {
  task: MaintenanceTaskDto | null;
  defaultEquipmentId?: string;
  onClose: () => void;
  onSaved: () => void;
}

interface ReminderRow {
  id?: string;
  leadTimeValue: number;
  leadTimeUnit: LeadTimeUnit;
}

export const TaskFormDialog: React.FC<Props> = ({ task, defaultEquipmentId, onClose, onSaved }) => {
  const isEdit = task != null;
  const [equipments, setEquipments] = React.useState<EquipmentDto[]>([]);
  const [equipmentId, setEquipmentId] = React.useState(task?.equipmentId ?? defaultEquipmentId ?? "");
  const [title, setTitle] = React.useState(task?.title ?? "");
  const [description, setDescription] = React.useState(task?.description ?? "");
  const [category, setCategory] = React.useState<MaintenanceCategory>(task ? intToCategory(task.category) : "Other");
  const [priority, setPriority] = React.useState<MaintenancePriority>(task ? intToPriority(task.priority) : "Normal");
  const [recurrenceType, setRecurrenceType] = React.useState<RecurrenceType>(task ? intToRecurrence(task.recurrenceType) : "Interval");
  const [intervalValue, setIntervalValue] = React.useState<number | null>(task?.intervalValue ?? 1);
  const [intervalUnit, setIntervalUnit] = React.useState<IntervalUnit | null>(
    task ? intToIntervalUnit(task.intervalUnit ?? null) : "Months"
  );
  const [nextDueAt, setNextDueAt] = React.useState<string>(task?.nextDueAt
    ? task.nextDueAt.slice(0, 10)
    : new Date().toISOString().slice(0, 10));
  const [isActive, setIsActive] = React.useState(task?.isActive ?? true);
  const [reminders, setReminders] = React.useState<ReminderRow[]>(
    task?.reminders.map((r) => ({
      id: r.id,
      leadTimeValue: r.leadTimeValue,
      leadTimeUnit: intToLeadTimeUnit(r.leadTimeUnit),
    })) ?? [{ leadTimeValue: 1, leadTimeUnit: "Days" }]
  );
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    void equipmentApi.search({}).then(setEquipments).catch(() => setEquipments([]));
  }, []);

  const handleSave = async () => {
    if (!title.trim()) { window.alert("El título es obligatorio"); return; }
    if (!equipmentId) { window.alert("Selecciona un equipo"); return; }
    if (!nextDueAt) { window.alert("Indica la próxima fecha de vencimiento"); return; }
    if (recurrenceType === "Interval" && (!intervalValue || intervalValue < 1 || !intervalUnit)) {
      window.alert("Para tareas recurrentes, indica intervalo y unidad");
      return;
    }

    setSaving(true);
    try {
      const payload: CreateMaintenanceTaskDto = {
        equipmentId,
        title: title.trim(),
        description: description.trim() || null,
        category: categoryToInt(category),
        priority: priorityToInt(priority),
        recurrenceType: recurrenceToInt(recurrenceType),
        intervalValue: recurrenceType === "Interval" ? intervalValue : null,
        intervalUnit: recurrenceType === "Interval" && intervalUnit ? intervalUnitToInt(intervalUnit) : null,
        nextDueAt: new Date(nextDueAt + "T09:00:00").toISOString(),
        isActive,
        reminders: remindersToApi(reminders),
      };

      if (isEdit && task) {
        await maintenanceTasksApi.update(task.id, payload);
      } else {
        await maintenanceTasksApi.create(payload);
      }
      onSaved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar tarea de mantenimiento" : "Nueva tarea de mantenimiento"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Equipo *</Label>
            <Select value={equipmentId} onValueChange={setEquipmentId} disabled={isEdit}>
              <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                {equipments.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} <span className="text-slate-400">({e.areaName})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Título *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Cambio de escobillas, ITV" />
          </div>

          <div>
            <Label>Categoría</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as MaintenanceCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MAINTENANCE_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Prioridad</Label>
            <Select value={priority} onValueChange={(v) => setPriority(v as MaintenancePriority)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MAINTENANCE_PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <RecurrenceEditor
              recurrenceType={recurrenceType}
              intervalValue={intervalValue}
              intervalUnit={intervalUnit}
              onChange={(s) => {
                setRecurrenceType(s.recurrenceType);
                setIntervalValue(s.intervalValue);
                setIntervalUnit(s.intervalUnit);
              }}
            />
          </div>

          <div className="md:col-span-2">
            <Label>Próximo vencimiento *</Label>
            <Input type="date" value={nextDueAt} onChange={(e) => setNextDueAt(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <Label>Descripción</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <ReminderEditor reminders={reminders} onChange={setReminders} />
          </div>

          <div className="flex items-center gap-2 md:col-span-2">
            <Checkbox id="task-active" checked={isActive} onCheckedChange={(c) => setIsActive(c === true)} />
            <Label htmlFor="task-active">Activa (genera avisos)</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Guardando…" : isEdit ? "Guardar" : "Crear"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
